import { useState, useCallback, useEffect, ReactElement } from 'react';
import { useRoutes, useAwsEffect } from '@localstack/services';
import { Card, Button } from '@mui/material';
import { Dropdown, ButtonWithDialog, IAMRolesTable, PageTitle } from '@localstack/ui';
import { IAMRole, IAMListRolesResponse } from '@localstack/types';

import { DEFAULT_IAM_ROUTES } from '@localstack/constants';

import { NavTabs } from './components';
import { IAMProps } from './types';

const PAGE_SIZE = 100;

export const IAMRoles = ({ Layout, clientOverrides, routes = DEFAULT_IAM_ROUTES }: IAMProps): ReactElement => {
  const [roles, setRoles] = useState<IAMRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  const { goto } = useRoutes();

  const { listRoles } = useAwsEffect('IAM', ['listRoles'], { clientOverrides });

  let response: IAMListRolesResponse = { Roles: [], IsTruncated: true, Marker: undefined };

  const listAllRoles = async () => {
    const allRoles: IAMRole[] = [];

    setIsLoading(true);
    while (response.IsTruncated) {
      response = await listRoles({ MaxItems: PAGE_SIZE, Marker: response.Marker });
      allRoles.push(...(response.Roles ?? []));
    }

    setRoles(allRoles);
    setIsLoading(false);
  };

  useEffect(() => {
    listAllRoles();
  }, []);

  const { deleteRole } = useAwsEffect('IAM', ['deleteRole'], {
    revalidate: ['getRole', 'listRoles'],
    clientOverrides,
  });

  const handleDeleteSelected = useCallback(async () => {
    const promises = selectedIds.map((RoleName) => deleteRole({ RoleName }));
    await Promise.all(promises);
    listAllRoles();
  }, [selectedIds]);

  return (
    <Layout
      documentTitle="IAM: Roles"
      title={
        <PageTitle
          title="IAM Roles"
          onMutate={listAllRoles}
          breadcrumbs={[
            ['IAM', () => goto(routes.RESOURCES_IAM)],
            ['Roles', () => goto(routes.RESOURCES_IAM_ROLES)],
          ]}
        />
      }
      tabs={<NavTabs routes={routes} />}
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_IAM_ROLE_CREATE)}>
            Create Role
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} Role(s)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected Roles will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card style={{ height: '100%' }}>
        <IAMRolesTable
          selectable
          loading={isLoading}
          roles={roles}
          onSelect={setSelectedIds}
          page={page}
          onPageChange={setPage}
          pageSize={PAGE_SIZE}
          onViewRole={({ RoleName }) => goto(routes.RESOURCES_IAM_ROLE, { roleName: RoleName })}
        />
      </Card>
    </Layout>
  );
};
